/**
 * Delivery methods. Add a new carrier by:
 *   1. Creating `src/sections/delivery/providers/<id>.js` exporting { validate(state), label(state) }
 *   2. Adding an object to this array with a matching `provider` import.
 *
 * subOptions: each can independently declare which inputs the city/branch UI shows.
 * `kind: 'postomat'` marks a parcel-locker tab — it drives both the field label
 * and the `kind=` sent to the carrier proxy. Anything without it is a branch.
 * Declare it here rather than testing sub-option ids in the renderer, or every
 * new carrier silently gets the wrong list.
 *
 * Branding: `logoSrc` points at an SVG in public/carriers/. When it is set the
 * picker shows the logo instead of the coloured text chip. `logoHasName: true`
 * means the artwork already contains the carrier's name, so the renderer omits
 * the text label next to it (otherwise "Нова Пошта" would appear twice).
 * `logoText`/`logoBg` are kept as the fallback for any carrier without art,
 * and are still what the thank-you page renders.
 */
export const DELIVERY_METHODS = [
  {
    id: 'novaposhta',
    name: 'Нова Пошта',
    logoText: 'НОВА ПОШТА',
    logoBg: '#d6232a',
    logoColor: '#fff',
    logoSrc: '/carriers/novaposhta.svg',
    logoHasName: true,
    provider: () => import('../sections/delivery/providers/novaposhta.js'),
    subOptions: [
      { id: 'np-branch',    label: 'Відділення',       requiresCity: true,  requiresBranch: true,  requiresAddress: false },
      { id: 'np-poshtomat', label: 'Поштомат',         requiresCity: true,  requiresBranch: true,  requiresAddress: false, kind: 'postomat' },
      { id: 'np-address',   label: 'Адресна доставка', requiresCity: true,  requiresBranch: false, requiresAddress: true  },
    ],
  },
  {
    id: 'ukrposhta',
    name: 'Укрпошта',
    logoText: 'УКРПОШТА',
    logoBg: '#ffd400',
    logoColor: '#1a1a1a',
    logoSrc: '/carriers/ukrposhta.svg',
    logoHasName: true,
    provider: () => import('../sections/delivery/providers/ukrposhta.js'),
    subOptions: [
      { id: 'up-branch',  label: 'Відділення',       requiresCity: true, requiresBranch: true,  requiresAddress: false },
      // Disabled until we wire up the address-delivery flow on Ukrposhta.
      { id: 'up-address', label: 'Адресна доставка', requiresCity: true, requiresBranch: false, requiresAddress: true,
        disabled: true, notice: 'Тимчасово недоступно' },
    ],
  },
  {
    id: 'rozetka',
    name: 'Rozetka Delivery',
    logoText: 'ROZETKA',
    logoBg: '#00a046',
    logoColor: '#fff',
    logoSrc: '/carriers/rozetka.svg',
    // The supplied artwork is the round mark only, with no wordmark, so the
    // name is rendered beside it.
    logoHasName: false,
    provider: () => import('../sections/delivery/providers/rozetka.js'),
    subOptions: [
      // Branch-only carrier: Rozetka's directory has no lockers and no street
      // search, so there is nothing to offer beyond pickup points.
      { id: 'rz-branch', label: 'Відділення', requiresCity: true, requiresBranch: true, requiresAddress: false },
    ],
  },
  {
    id: 'meest',
    name: 'Meest',
    logoText: 'MEEST',
    logoBg: '#0061af',
    logoColor: '#fff',
    logoSrc: '/carriers/meest.svg',
    logoHasName: true,
    provider: () => import('../sections/delivery/providers/meest.js'),
    subOptions: [
      { id: 'ms-branch',    label: 'Відділення',       requiresCity: true, requiresBranch: true,  requiresAddress: false },
      { id: 'ms-poshtomat', label: 'Поштомат',         requiresCity: true, requiresBranch: true,  requiresAddress: false, kind: 'postomat' },
      { id: 'ms-address',   label: 'Адресна доставка', requiresCity: true, requiresBranch: false, requiresAddress: true  },
    ],
  },
];
